/**
 * [S19 2026-09-14] WSPÓLNY ZEGAR WSKAŹNIKÓW ŁADOWANIA.
 *
 * Polecenie właściciela (13.09 wieczór, S19): „poprawił mi loader który jest w aplikacji, na jakiś normalny, process bar
 * ładowania, z czasem […] to ma być coś smukłego biznesowego […] te skaczące kropki są ok i je zastosuj”.
 *
 * Ten plik trzyma WYŁĄCZNIE liczby i rachunek czasu — bez wyglądu — żeby pasek postępu, licznik i globalny wskaźnik
 * liczyły z JEDNEGO źródła. Dwa zegary obok siebie potrafiłyby pokazać „1,4 s” w pigułce i pasek z innego momentu.
 */
import { useEffect, useState } from "react";

/** Licznik czasu pojawia się dopiero po tylu ms — zapytanie trwające 150 ms nie ma migać cyferkami. */
export const OPOZNIENIE_LICZNIKA_MS = 400;

/** Po tylu ms wskaźnik dopisuje „Trwa dłużej niż zwykle…”, żeby człowiek wiedział, że to nie zawieszenie. */
export const PROG_DLUGIEGO_LADOWANIA_MS = 10000;

/**
 * Bezpiecznik globalnego wskaźnika: po tylu ms ciągłego ładowania licznik aktywnych ładowań się zeruje.
 * Dlaczego: LoadingContext liczy pary showLoader/hideLoader. Ekran, który zgubi hideLoader (wyjątek przed
 * `finally`, odmontowanie w trakcie), zostawiłby pasek u góry NA ZAWSZE. Stary boolean gasił wszystko pierwszym
 * hideLoader, więc taki błąd był niewidoczny — licznik by go ujawnił w najgorszy możliwy sposób.
 */
export const BEZPIECZNIK_LADOWANIA_MS = 60000;

/** Co ile ms odświeżamy licznik i pasek. 100 ms = płynna dziesiąta część sekundy bez kosztu. */
export const KROK_ZEGARA_MS = 100;

/**
 * Czas trwania po polsku: „0,4 s”, „1,5 s”, „12 s”, „1 min 05 s”.
 * Poniżej 10 s z jedną cyfrą po PRZECINKU (polski zapis dziesiętny), potem pełne sekundy.
 * @param {number} ms
 * @returns {string}
 */
export const formatujCzasLadowania = (ms) => {
    const wartosc = Number.isFinite(ms) && ms > 0 ? ms : 0;
    if (wartosc < 10000) return `${(Math.floor(wartosc / 100) / 10).toFixed(1).replace(".", ",")} s`;
    if (wartosc < 60000) return `${Math.floor(wartosc / 1000)} s`;
    const minuty = Math.floor(wartosc / 60000);
    const sekundy = Math.floor((wartosc % 60000) / 1000);
    return `${minuty} min ${String(sekundy).padStart(2, "0")} s`;
};

/**
 * Postęp paska dla czasu trwania: rośnie szybko na początku i zwalnia, zbliżając się do 90% — nigdy do 100%,
 * bo nie wiemy, ile zostało. Pełne 100% pasek dostaje dopiero po zakończeniu (PasekPostepu, faza „koniec”).
 * Start od 8%, żeby pasek był widoczny od pierwszej klatki.
 * @param {number} ms
 * @returns {number} ułamek 0.08..0.9
 */
export const postepDlaCzasu = (ms) => {
    const t = Number.isFinite(ms) && ms > 0 ? ms : 0;
    return Math.min(0.9, 0.08 + 0.82 * (1 - Math.exp(-t / 4000)));
};

/**
 * Upływ czasu od `startedAt` w ms, odświeżany co {@link KROK_ZEGARA_MS}.
 * `startedAt == null` = zegar stoi (zwraca 0 i nie trzyma żadnego interwału).
 * @param {number|null|undefined} startedAt znacznik Date.now() początku ładowania
 * @returns {number}
 */
export const useUplywCzasu = (startedAt) => {
    const [teraz, setTeraz] = useState(() => Date.now());

    useEffect(() => {
        if (startedAt == null) return undefined;
        setTeraz(Date.now());
        const id = window.setInterval(() => setTeraz(Date.now()), KROK_ZEGARA_MS);
        return () => window.clearInterval(id);
    }, [startedAt]);

    return startedAt == null ? 0 : Math.max(0, teraz - startedAt);
};
